<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { debouncedWatch, isClient, useMouse } from '@vueuse/core'
import type { Maybe, Sg_MenuLink, Sg_TopBarItem } from '~/schema/types/generated'
import { useMenuStore } from '~/stores/menu'
import type { IClientType } from '~/components/PlusBlog/lp/atom/ClientTypeList/index.vue'
import { useIplusMobileBanner } from '~/composables/blog/useIplusMobileBanner'

const storeMenu = useMenuStore()
const route = useRoute()
const isSoho = computed(() => {
    return !!route.path.includes('dla-firm')
})

const { fetchAllMenuData, setIsIplusClosed, setIsIplusHiddenOnScroll } = storeMenu
const { menuData, isMenuMobileOpen, isIplusVisible, isMenuDesktopOpen } = storeToRefs(storeMenu)

await useAsyncData('component-menu-data', async () => {
    await fetchAllMenuData()
    return true
})

const { sendMenuEvent } = useSendEvent()

const isMenuHidden = ref<boolean>(false)
const scrollPosition = ref<number>(0)
const { y: mouseY } = useMouse()
const { y: windowY } = useWindowScroll()

useHead({
    bodyAttrs: {
        class: computed(() => {
            return isMenuMobileOpen.value ? 'max-md:fixed max-md:max-w-full' : ''
        }),
    },
})

debouncedWatch(
    [mouseY, windowY],
    ([mouse, window]) => {
        if (mouse - window < 60) {
            isMenuHidden.value = false
        }
    },
    { debounce: 100 },
)

function handleMenuOnScroll() {
    if (isMenuDesktopOpen.value) {
        return
    }

    const currentScrollPosition = window.scrollY

    if (currentScrollPosition > 0 && currentScrollPosition > scrollPosition.value) {
        // scroll down
        isMenuHidden.value = true
        setIsIplusHiddenOnScroll(false)
    } else {
        // scroll up || menu open
        isMenuHidden.value = false
        if (!isMenuMobileOpen.value) {
            setIsIplusHiddenOnScroll(true)
        }
    }

    scrollPosition.value = currentScrollPosition
}

onBeforeMount(() => {
    if (isClient) {
        window.addEventListener('scroll', handleMenuOnScroll)
    }
})

onBeforeUnmount(() => {
    if (isClient) {
        window.removeEventListener('scroll', handleMenuOnScroll)
    }
})

const clientTypeList = [
    {
        id: 'ind',
        text: 'Klient indywidualny',
        url: 'https://plus.pl',
    },
    {
        id: 'soho',
        text: 'Małe firmy',
        url: 'https://plus.pl/dla-firm',
    },
    {
        id: 'soho-big',
        text: 'Średnie i duże firmy',
        url: 'https://biznes.plus.pl/',
    },
] as IClientType[]

const menuLinksList = computed<Sg_TopBarItem[]>(() => {
    if (!menuData.value) {
        return []
    }
    return (
        (isSoho.value
            ? menuData.value?.find((item) => item?.name === 'SOHO')
            : menuData.value?.find((item) => item?.name === 'IND')
        )?.sg_topBarItems ?? []
    ).filter(Boolean) as Sg_TopBarItem[]
})

const mobileMenuClasses = computed(() => {
    switch (true) {
        case isIplusVisible.value && isMenuMobileOpen.value:
            return 'top-16 h-[calc(100dvh-var(--lp-menu-iplus-app))]'
        case !isIplusVisible.value && isMenuMobileOpen.value:
            return 'top-0 h-dvh'
        default:
            return '-top-[100dvh] h-screen'
    }
})

const mobileMenuListClasses = computed(() => {
    return isIplusVisible.value
        ? 'h-[calc(100dvh-var(--lp-menu-bar-mobile)-var(--lp-menu-bottom-bar-mobile)-var(--lp-menu-iplus-app))]'
        : 'h-[calc(100dvh-var(--lp-menu-bar-mobile)-var(--lp-menu-bottom-bar-mobile))]'
})

const getHeaderClasses = computed<{ [className: string]: boolean }>(() => ({
    'max-lg:top-16': isIplusVisible.value,
    'max-lg:top-0': !isIplusVisible.value,
    'lg:top-0': !isMenuHidden.value,
    'lg:-top-28': isMenuHidden.value,
}))

onMounted(() => {
    useIplusMobileBanner()
})
</script>

<template>
    <!-- margin from top -->
    <div
        class="w-full lg:h-[80px]"
        :class="isIplusVisible ? 'h-[112px] md:h-[152px]' : 'h-[40px]'"
    />
    <!-- background for iplus mobile useful on animation on scroll -->
    <div
        class="fixed left-0 z-10 h-16 w-full bg-plus-lp-grey-100 lg:hidden"
        :class="isIplusVisible ? 'top-0' : '-mt-28'"
    />
    <PlusBlogLpMoleculeIPlusMobile
        class="plus-lp bg-plus-lp-background !font-figtree text-plus-lp-dark-blue transition-all duration-500"
        :class="isIplusVisible ? 'top-0' : '-mt-28'"
        @handle-close="setIsIplusClosed(true)"
    />
    <header
        class="fixed left-0 z-50 w-screen bg-plus-lp-background !font-figtree text-plus-lp-dark-blue transition-all duration-400"
        :class="getHeaderClasses"
    >
        <div class="relative z-20 hidden h-[--lp-menu-bar-client-type] bg-plus-lp-grey-100 md:flex md:items-center">
            <div class="plus-lp-container">
                <PlusBlogLpAtomClientTypeList :list="clientTypeList" />
            </div>
        </div>

        <PlusBlogLpOrganismMenuTopBar :menu-section="menuLinksList" />

        <Teleport to="#teleports">
            <nav
                :class="mobileMenuClasses"
                class="fixed z-40 flex w-screen flex-col justify-between overflow-auto bg-plus-lp-white pt-16 transition-all duration-400 md:hidden"
            >
                <div
                    class="overflow-auto"
                    :class="mobileMenuListClasses"
                >
                    <PlusBlogLpMoleculeMenuCategories :categories="menuLinksList" />
                </div>
                <div class="shadow-default mt-2 h-[--lp-menu-bottom-bar-mobile] px-3 pb-8 pt-4">
                    <PlusBlogLpMoleculeClientTypeSelect :list="clientTypeList" />
                    <a
                        href="https://www.plus.pl/udogodnienia"
                        class="mt-2 flex items-center gap-2 py-2 text-lp-label-md font-medium"
                        @click="sendMenuEvent('udogodnienia', '/udogodnienia')"
                    >
                        <img
                            src="https://api.plus.pl/file/images_lp/plus_acse_landingpages/menu/accessibility_icon.svg"
                            alt="dotępnośc cyfrowa"
                        />
                        <span class="!font-figtree">Deklaracja dostępności cyfrowej</span>
                    </a>
                </div>
            </nav>
        </Teleport>
    </header>
</template>
